import styled from 'styled-components';
import {
  LinkTrackingData,
  SearchCardType,
  TrackingDataEvent,
} from '../../../../../../common/AnalyticsContext/LinkTracking';
import { useSearchFilters } from '../../../../../../common/components/SearchHeader/filters/useSearchFilters';
import { COVID_19_FLU_VACCINATIONS } from '../../../../../../common/components/SearchHeader/types/FilterChoice';
import { formatName } from '../../../../../../common/formatName';
import { addQueryParams } from '../../../../../../common/routing/addQueryParams';
import { SearchResultPractitioner } from '../../../../../types';
import { makePractitionerContactUrl, addPracticeIdToPractitionerUrl } from '../../../../../urls';
import { Address } from '../Address/Address';
import { Appointments } from '../Appointments/Appointments';
import { Portrait } from '../Portrait/Portrait';
import { ProfileName } from '../ProfileName/ProfileName';
import { Subheading } from '../Subheading/Subheading';
import { Card, CardBody, CardLeftColumn, CardRightColumn, CardSection } from '../card.styles';
import { PractitionerDetails } from '../details/PractitionerDetails';
import { formatSubheading } from './formatSubheading';
import { usePracticeForSearchLocation } from './usePracticeForSearchLocation';

export interface Props {
  index?: number;
  practitioner: SearchResultPractitioner;
  searchSpecialty?: string;
  resultType: 'standard' | 'extended';
  inSearchedSuburb?: boolean;
}

export const PractitionerCard = ({
  practitioner,
  index,
  searchSpecialty,
  resultType,
  inSearchedSuburb,
}: Props) => {
  const {
    appointmentSummary,
    bookingBar,
    firstName,
    gender,
    id,
    lastName,
    profilePhoto,
    specialties,
    title,
    url,
    rankingInfo,
  } = practitioner;

  const [filters] = useSearchFilters();
  const practice = usePracticeForSearchLocation(practitioner);

  const vaccineType =
    filters.topic?.specialist === COVID_19_FLU_VACCINATIONS ? filters.vaccineType : undefined;

  const composedUrl = addQueryParams(addPracticeIdToPractitionerUrl(url, practice?.id), {
    vaccineType,
  });

  const address = practice?.address;

  const name = formatName(title, firstName, lastName);
  const subheading = formatSubheading(specialties, gender);

  const phoneNumber = practice?.phoneNumber;

  const clickTracking: LinkTrackingData = {
    event: TrackingDataEvent.SEARCH_RESULT_CARD,
    searchCardType: SearchCardType.PRACTITIONER,
    searchCardFeatured: false,
    searchCardHasAppointments: appointmentSummary !== null,
    searchCardResultsPosition: (index || 0) + 1,
    practitionerId: id,
    practitionerName: name,
    searchResultType: resultType,
    searchResultIsInSuburb: inSearchedSuburb,
  };

  return (
    <Card>
      <CardBody>
        <CardSection>
          <CardLeftColumn>
            <Subheading>{subheading}</Subheading>

            <ProfileName href={composedUrl} trackingData={clickTracking}>
              {name}
            </ProfileName>

            {address && (
              <AddressWrapper>
                <Address
                  address={address}
                  distance={(rankingInfo?.geoDistance ?? 0) / 1000}
                  url={makePractitionerContactUrl(url)}
                />
              </AddressWrapper>
            )}
          </CardLeftColumn>

          <CardRightColumn>{profilePhoto && <Portrait image={profilePhoto} />}</CardRightColumn>
        </CardSection>

        <CardSection>
          <PractitionerDetails practitioner={practitioner} />
        </CardSection>
      </CardBody>

      <Appointments
        appointmentSummary={appointmentSummary}
        barType={bookingBar}
        id={id}
        name={name}
        phone={phoneNumber}
        profileUrl={url}
        searchSpecialty={searchSpecialty ?? specialties[0] ?? ''}
        trackingData={clickTracking}
        type="practitioner"
      />
    </Card>
  );
};

const AddressWrapper = styled.div`
  margin-top: 8px;
`;
